import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

const Navigation = styled.nav<{ variant?: 'default' | 'paper' }>`
  position: sticky;
  top: max(1rem, env(safe-area-inset-top));
  background: ${props => props.variant === 'paper' 
    ? 'rgba(247, 243, 235, 0.98)' 
    : 'rgba(0, 0, 0, 0.4)'};
  padding: 0.75rem;
  border: ${props => props.variant === 'paper' 
    ? '1px solid rgba(109, 107, 73, 0.25)' 
    : '1px solid rgba(255, 255, 255, 0.1)'};
  border-radius: 4px;
  margin-bottom: 1.5rem;
  z-index: 10;
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.variant === 'paper' 
    ? '0 2px 12px rgba(109, 107, 73, 0.1)' 
    : 'none'};
  display: none;

  @media (min-width: 768px) {
    display: block;
    top: 4rem;
    margin-bottom: 2rem;
    padding: 1.5rem;
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;

  @media (min-width: 768px) {
    gap: 1.5rem;
  }
`;

const NavItem = styled.li`
  margin: 0;
`;

const NavLink = styled.a<{ isActive: boolean; variant?: 'default' | 'paper' }>`
  color: ${props => props.variant === 'paper' 
    ? (props.isActive ? '#2c2a26' : '#6D6B49')
    : (props.isActive ? '#fff' : 'rgba(255, 255, 255, 0.7)')};
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  background: ${props => props.variant === 'paper'
    ? (props.isActive ? 'rgba(244, 199, 70, 0.2)' : 'transparent')
    : (props.isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent')};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
    background: ${props => props.variant === 'paper' 
      ? 'rgba(244, 199, 70, 0.15)' 
      : 'rgba(255, 255, 255, 0.15)'};
  }

  @media (min-width: 768px) {
    font-size: 1rem;
    padding: 0.5rem 1rem;
  }
`;

const MobileNavButton = styled.button<{ variant?: 'default' | 'paper' }>`
  position: fixed;
  top: max(1rem, env(safe-area-inset-top));
  right: 1rem;
  z-index: 20;
  background: ${props => props.variant === 'paper' 
    ? 'rgba(247, 243, 235, 0.98)' 
    : 'rgba(0, 0, 0, 0.4)'};
  border: ${props => props.variant === 'paper' 
    ? '1px solid rgba(109, 107, 73, 0.3)' 
    : '1px solid rgba(255, 255, 255, 0.1)'};
  padding: 0.75rem;
  min-width: 44px;
  min-height: 44px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  backdrop-filter: blur(10px);

  @media (min-width: 768px) {
    display: none;
  }
`;

const NavLine = styled.span<{ variant?: 'default' | 'paper' }>`
  display: block;
  width: 24px;
  height: 2px;
  background: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  transition: all 0.3s ease;
`;

const MobileNav = styled(motion.div)<{ variant?: 'default' | 'paper' }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(85vw, 320px);
  height: 100vh;
  height: 100dvh;
  background: ${props => props.variant === 'paper' 
    ? 'rgba(247, 243, 235, 0.98)' 
    : 'rgba(0, 0, 0, 0.95)'};
  padding: max(5rem, calc(env(safe-area-inset-top) + 3rem)) 1.5rem 2rem;
  padding-bottom: max(2rem, env(safe-area-inset-bottom));
  z-index: 15;
  backdrop-filter: blur(10px);
  overflow-y: auto;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileNavItem = styled.li`
  margin: 0;
`;

const MobileNavLink = styled.a<{ isActive: boolean; variant?: 'default' | 'paper' }>`
  color: ${props => props.variant === 'paper'
    ? (props.isActive ? '#2c2a26' : '#6D6B49')
    : (props.isActive ? '#fff' : 'rgba(255, 255, 255, 0.7)')};
  text-decoration: none;
  font-size: 1rem;
  display: flex;
  align-items: center;
  padding: 1rem 0;
  min-height: 48px;
  border-bottom: 1px solid ${props => props.variant === 'paper' 
    ? 'rgba(109, 107, 73, 0.2)' 
    : 'rgba(255, 255, 255, 0.1)'};
  transition: color 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  }

  &.active {
    color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 14;
  backdrop-filter: blur(5px);
  display: none;

  @media (max-width: 767px) {
    display: block;
  }
`;

interface NavigationItem {
  id: string;
  name: string;
}

interface ExhibitionNavigationProps {
  items: NavigationItem[];
  activeId: string | null;
  onItemClick: (id: string) => void;
  basePath?: string;
  variant?: 'default' | 'paper';
}

export const ExhibitionNavigation = ({
  items,
  activeId,
  onItemClick,
  basePath = '/exhibitions/the-smoke',
  variant = 'default',
}: ExhibitionNavigationProps) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const handleItemClick = (id: string) => {
    onItemClick(id);
    setIsMobileNavOpen(false);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <Navigation variant={variant}>
        <NavList>
          {items.map(item => (
            <NavItem key={item.id}>
              <NavLink
                href={`${basePath}/${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleItemClick(item.id);
                }}
                isActive={activeId === item.id}
                variant={variant}
              >
                {item.name}
              </NavLink>
            </NavItem>
          ))}
        </NavList>
      </Navigation>

      {/* Mobile Navigation Button */}
      <MobileNavButton 
        variant={variant}
        onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
      >
        <NavLine variant={variant} style={{ transform: isMobileNavOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
        <NavLine variant={variant} style={{ opacity: isMobileNavOpen ? 0 : 1 }} />
        <NavLine variant={variant} style={{ transform: isMobileNavOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
      </MobileNavButton>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {isMobileNavOpen && (
          <>
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileNavOpen(false)}
            />
            <MobileNav
              variant={variant}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 20 }}
            >
              <MobileNavList>
                {items.map(item => (
                  <MobileNavItem key={item.id}>
                    <MobileNavLink
                      href={`${basePath}/${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(item.id);
                      }}
                      isActive={activeId === item.id}
                      variant={variant}
                    >
                      {item.name}
                    </MobileNavLink>
                  </MobileNavItem>
                ))}
              </MobileNavList>
            </MobileNav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}; 